import { Link, NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Home, Sparkles, ListChecks, Globe, ScrollText, LogOut, Settings as SettingsIcon, User, Activity, ShieldCheck, BookOpen, MessageCircle, Menu, X } from "lucide-react";
import api from "@/lib/api";
import Logo, { FoxMark, Wordmark } from "@/components/Logo";

const navItems = [
    { to: "/", label: "Home", icon: Home, testid: "nav-home", end: true },
    { to: "/generate", label: "Generate", icon: Sparkles, testid: "nav-generate" },
    { to: "/listicles", label: "Recipe listicles", icon: BookOpen, testid: "nav-listicles" },
    { to: "/queue", label: "Queue", icon: ListChecks, testid: "nav-queue" },
    { to: "/sites", label: "WP Sites", icon: Globe, testid: "nav-sites" },
    { to: "/analytics", label: "Analytics", icon: Activity, testid: "nav-analytics" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
    { to: "/account", label: "Account", icon: User, testid: "nav-account" },
    { to: "/logs", label: "Logs", icon: ScrollText, testid: "nav-logs" },
];

const helpItems = [
    { to: "/get-started", label: "Get started", icon: BookOpen, testid: "nav-getstarted" },
    { to: "/contact", label: "Support", icon: MessageCircle, testid: "nav-support" },
];

export default function Layout({ embed }) {
    const { user, logout } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        api.get("/auth/is-admin").then(r => setIsAdmin(!!r.data.is_admin)).catch(() => {});
    }, []); // mount-only - admin status checked once at login

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-paper" data-testid="app-layout">
            <aside className="w-full md:w-64 md:shrink-0 border-b md:border-b-0 md:border-r border-line bg-white flex flex-col" data-testid="sidebar">
                <div className="p-5 md:p-6 border-b border-line flex items-center justify-between gap-3">
                    <Link to="/generate" className="flex items-center gap-3" data-testid="brand-link">
                        <FoxMark size={36} color="#111111" />
                        <div>
                            <Wordmark className="text-base" />
                            <div className="overline text-[10px] mt-0.5">SEO recipe pipeline</div>
                        </div>
                    </Link>
                    <button className="md:hidden p-2 hover:bg-paper" aria-label="Toggle navigation" aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-navigation-toggle">{menuOpen ? <X size={20} /> : <Menu size={20} />}</button>
                </div>
                <nav onClick={() => setMenuOpen(false)} className={`flex-1 p-3 grid-cols-2 gap-1 md:block md:space-y-1 ${menuOpen ? "grid" : "hidden"}`}>
                    {navItems.map(({ to, label, icon: Icon, testid, end }) => (
                        <NavLink key={to} to={to} end={end} data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                    isActive
                                        ? "bg-ink text-white"
                                        : "text-ink2 hover:bg-paper hover:text-ink"
                                }`
                            }>
                            <Icon className="w-4 h-4" />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                    {isAdmin && (
                        <NavLink to="/admin" data-testid="nav-admin"
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                    isActive
                                        ? "bg-klein text-white"
                                        : "text-klein hover:bg-klein/10"
                                }`
                            }>
                            <ShieldCheck className="w-4 h-4" />
                            <span>Admin</span>
                        </NavLink>
                    )}
                    <div className="pt-3 mt-3 border-t border-line">
                        <div className="px-3 py-1 overline text-[10px] text-ink2">Help</div>
                        {helpItems.map(({ to, label, icon: Icon, testid }) => (
                            <NavLink key={to} to={to} data-testid={testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                                        isActive
                                            ? "bg-ink text-white"
                                            : "text-ink2 hover:bg-paper hover:text-ink"
                                    }`
                                }>
                                <Icon className="w-4 h-4" />
                                <span>{label}</span>
                            </NavLink>
                        ))}
                    </div>
                </nav>
                <div className={`p-3 border-t border-line md:block ${menuOpen ? "block" : "hidden"}`}>
                    <div className="px-3 py-2 text-xs text-ink2 font-mono truncate" data-testid="layout-user-email">
                        {user?.email}
                    </div>
                    <Button variant="ghost" onClick={logout}
                        className="w-full justify-start rounded-none text-ink2 hover:text-ink"
                        data-testid="logout-button">
                        <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </Button>
                </div>
            </aside>
            <main className="flex-1 min-w-0" data-testid="main-content">
                {embed ?? <Outlet />}
            </main>
        </div>
    );
}
